<template>
  <Modal size="larger" @modalClose="onClose">
    <div class="modal-header">
      <h4 v-html="generateModalTitle()" />
      <button
        type="button"
        @click.stop="onClose"
        class="btn btn-link btm-sm btn-close-container">
        <i class="fas fa-times"></i>
      </button>
    </div>
    <form v-on:submit.prevent="onSubmit">
      <div class="modal-body">
        <div class="section">
          <div class="row" v-if="isCreateFromExistingProgram()">
            <div class="col-md-8">
              <ManageScheduleItemPrograms
                :scheduleItem.sync="newScheduleItem"
                :updateScheduleItemStateError.sync="updateScheduleItemStateError" />
            </div>
            <div class="col-md-4">
              <ManageScheduleTemplateItemOverrideProgram
                :scheduleItem.sync="newScheduleItem"
                :updateScheduleItemStateError.sync="updateScheduleItemStateError" />
            </div>
          </div>
          <div class="row">
            <div class="col-md-8">
              <FormGroup :errorMessage="updateScheduleItemStateError.programTitle">
                <h5 class="name">ชื่อ <small class="text-danger ml-auto">* ต้องระบุ</small></h5>
                <input
                  type="text"
                  placeholder="ชื่อ"
                  class="form-control"
                  :class="{'is-invalid': updateScheduleItemStateError.programTitle }"
                  v-model="newScheduleItem.programTitle"
                  @blur="checkRequired"
                  :disabled="!isEnableOverrideProgram()" />
                <div
                  class="invalid-feedback"
                  v-if="updateScheduleItemStateError.programTitle">
                  ระบุชื่อ
                </div>
              </FormGroup>
              <ManageScheduleItemContentCategory
                :scheduleItem.sync="newScheduleItem"
                :updateScheduleItemStateError.sync="updateScheduleItemStateError" />
              <!-- คุณสมบัติของรายการ -->
              <div class="form-group-container">
                <div class="form-group-item">
                  <ManageProgramFeatures
                    :features.sync="newScheduleItem.features"
                    :updateFeatureStateError.sync="updateScheduleItemStateError.features"
                    :disabled="!isEnableOverrideProgram()" />
                </div>
                <!-- คุณสมบัติของรายการ -->
                <div class="form-group-item">
                  <ManageProgramBroadcasting
                    :broadcastingChannels.sync="newScheduleItem.broadcastingChannels"
                    :updateBroadcastingStateError.sync="updateScheduleItemStateError.broadcastingChannels"
                    :disabled="!isEnableOverrideProgram()" />
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <ImageField
                :disabledChangeImage="!isEnableOverrideProgram()"
                :image.sync="newScheduleItem.coverImageCustom"
                :defaultImage="newScheduleItem.coverImage" />
            </div>
          </div>
        </div>
        <div class="section section-on-air">
          <FormGroup>
            <div class="form-check">
              <input type="checkbox"
                class="form-check-input"
                v-model="newScheduleItem.isRerun">
              <label class="form-check-label">
                เป็นรายการฉายซ้ำ
              </label>
            </div>
          </FormGroup>
          <ManageScheduleTemplateItemOnAirOption
            :recurrentDaysOfWeek.sync="newScheduleItem.recurrentDaysOfWeek"
            :updateScheduleItemStateError.sync="updateScheduleItemStateError" />
          <!-- startTime / endTime -->
          <ManageScheduleItemTime
            :scheduleItem.sync="newScheduleItem"
            :updateScheduleItemStateError.sync="updateScheduleItemStateError" />
        </div>
      </div>
      <div class="modal-footer">
        <button type="button"
          class="btn btn-link pl-0"
          @click.stop="onClose">
          ยกเลิก
        </button>
        <button type="submit"
          class="btn btn-primary"
          :disabled="!isValid">
          {{getButtonLabel()}}
        </button>
      </div>
    </form>
  </Modal>
</template>

<script>
import _ from 'lodash';
import dateFormetter from '@/utils/dateFormatter';
import scheduleManagement from '@/utils/scheduleManagement';
import Modal from '@/components/Modal.vue';
import FormGroup from '@/components/FormGroup.vue';
import ImageField from '@/image/ImageField.vue';
import ManageProgramFeatures from '@/program/ManageProgramFeatures.vue';
import ManageProgramBroadcasting from '@/program/ManageProgramBroadcasting.vue';
import ManageScheduleItemTime from '@/schedule/scheduleItem/ManageScheduleItemTime.vue';
import ManageScheduleItemPrograms from '@/schedule/scheduleItem/ManageScheduleItemPrograms.vue';
import ManageScheduleItemContentCategory from '@/schedule/scheduleItem/ManageScheduleItemContentCategory.vue';

import ManageScheduleTemplateItemOnAirOption from './ManageScheduleTemplateItemOnAirOption.vue';
import ManageScheduleTemplateItemOverrideProgram from './ManageScheduleTemplateItemOverrideProgram.vue';

export default {
  mixins: [dateFormetter, scheduleManagement],
  props: {
    scheduleItem: {
      type: Object,
      default: () => {},
    },
    createScheduleType: {
      type: String,
      default: 'newSchedule', // existingProgram
    },
    onClose: {
      type: Function,
      default: () => {},
    },
    onUpdated: {
      type: Function,
      default: () => {},
    },
  },
  components: {
    Modal,
    FormGroup,
    ImageField,
    ManageProgramFeatures,
    ManageProgramBroadcasting,
    ManageScheduleItemTime,
    ManageScheduleItemPrograms,
    ManageScheduleTemplateItemOnAirOption,
    ManageScheduleTemplateItemOverrideProgram,
    ManageScheduleItemContentCategory,
  },
  data() {
    return {
      newScheduleItem: null,
      updateScheduleItemStateError: {
        programTitle: null,
      },
    };
  },
  created() {
    this.newScheduleItem = _.defaults(_.cloneDeep(this.scheduleItem),
      _.cloneDeep(this.defaultScheduleTemplateItem()));
    // map start time / end times to be datetime
    this.newScheduleItem = {
      ...this.newScheduleItem,
      ...this.mappingHourToTime(this.newScheduleItem),
    };
  },
  computed: {
    isValidRecurrentDaysOfWeek() {
      return this.newScheduleItem.recurrentDaysOfWeek && this.newScheduleItem.recurrentDaysOfWeek.length > 0;
    },
    isValidTitle() {
      return this.newScheduleItem.programTitle;
    },
    isValid() {
      return this.isValidTitle && this.isValidRecurrentDaysOfWeek;
    },
  },
  methods: {
    onSubmit() {
      // map start time / end times
      const newScheduleItem = {
        ...this.newScheduleItem,
        ...this.mappingTimeToHour(this.newScheduleItem),
      };

      this.onUpdated(newScheduleItem);
    },
    mappingTimeToHour({ startTime, endTime }) {
      return {
        startTime: this.formatScheduleTime(startTime),
        endTime: this.formatScheduleTime(endTime),
      };
    },
    isCreateFromExistingProgram() {
      return this.newScheduleItem.programId || this.createScheduleType === 'existingProgram';
    },
    getButtonLabel() {
      if (!_.isEmpty(this.scheduleItem)) {
        return 'ปิด';
      }
      return 'สร้าง';
    },
    generateModalTitle() {
      if (!_.isEmpty(this.scheduleItem)) {
        return `แก้ไขรายการ <strong>${this.scheduleItem.programTitle}</strong>`;
      }
      return 'สร้างรายการใหม่บนเทมเพลต';
    },
    checkRequired(event) {
      if (!event.target.value) {
        this.$emit('update:updateScheduleItemStateError', {
          ...this.updateScheduleItemStateError,
          programTitle: 'ระบุชื่อจริง',
        });
      } else {
        this.$emit('update:updateScheduleItemStateError', {
          ...this.updateScheduleItemStateError,
          programTitle: null,
        });
      }
    },
    isEnableOverrideProgram() {
      if (this.newScheduleItem.programId) {
        return this.newScheduleItem.overrideProgramProperties;
      }
      return true;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables.scss';


.form-group-rerun {
  margin-top: $spacer;
}


.form-group-container {
  display: flex;

  .form-group-item {
    margin-right: $spacer * 2;

    &:last-child {
      margin-right: 0;
    }
  }
}


.modal-body {
  padding: 0;

  .section {
    padding: $spacer;
  }

  .section-on-air {
    border-top: 1px solid $gray-200;
  }
}
</style>
